// frontend/src/services/yard.service.ts
import api from './api';
import { cachedGet } from '../utils/lookupCache';
import axios from 'axios';
import { config } from '../config/config';
import guardAuthService from './guardAuth.service';
import {
  YardLocation,
  YardLocationAdmin,
  MasterLocation,
  CheckInOutResponse,
  ProLookupResponse,
  YardInventoryItem,
  MissingContainerItem,
  YardBucketCounts,
  AddEntryRequest,
  UpdateEntryRequest,
  MarkOnYardRequest,
  ReeferLogRequest,
  ReeferLogResponse,
  ReeferLogCreateResponse,
  ReeferInYard,
  ReeferYard,
  YardEventItem,
  YardInventoryHistoryResponse,
} from '../types/yard';

class YardService {
  private baseUrl = '/yard';

  /**
   * Get auth headers - uses guard token if available, otherwise relies on api instance
   */
  private getGuardHeaders(): Record<string, string> {
    const guardToken = guardAuthService.getToken();
    if (guardToken) {
      return { Authorization: `Bearer ${guardToken}` };
    }
    return {};
  }

  /**
   * Get list of yard locations
   */
  async getYardLocations(includeLastEvent = false): Promise<YardLocation[]> {
    if (!includeLastEvent) {
      // Static yard list, fetched per mount on 5+ pages plus the guard kiosk
      // layout — shared 5-min cache. Also defense in depth: any future fetch
      // loop (see GuardLayout 2026-08-26) is capped at one request per TTL.
      const cached = await cachedGet<YardLocation[]>(`${this.baseUrl}/locations`);
      return cached.data;
    }
    // include_last_event carries per-yard last-activity data — keep it fresh.
    const response = await api.get<YardLocation[]>(`${this.baseUrl}/locations`, {
      params: { include_last_event: true },
    });
    return response.data;
  }

  /**
   * Look up order info by PRO number
   */
  async lookupPro(pronumber: number): Promise<ProLookupResponse | null> {
    try {
      const response = await api.get<ProLookupResponse>(
        `${this.baseUrl}/pro-lookup/${pronumber}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Add a new entry to yard inventory (without check-in event)
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint
   */
  async addEntry(request: AddEntryRequest): Promise<CheckInOutResponse> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      const guardToken = guardAuthService.getToken();
      const response = await axios.post<CheckInOutResponse>(
        `${config.API_URL}${this.baseUrl}/add-entry`,
        request,
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      const response = await api.post<CheckInOutResponse>(
        `${this.baseUrl}/add-entry/employee`,
        request
      );
      return response.data;
    }
  }

  /**
   * Update an existing yard inventory entry
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint
   */
  async updateEntry(recno: number, request: UpdateEntryRequest): Promise<CheckInOutResponse> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      const guardToken = guardAuthService.getToken();
      const response = await axios.put<CheckInOutResponse>(
        `${config.API_URL}${this.baseUrl}/inventory/${recno}`,
        request,
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      const response = await api.put<CheckInOutResponse>(
        `${this.baseUrl}/inventory/${recno}/employee`,
        request
      );
      return response.data;
    }
  }

  /**
   * Mark an inventory item as on-yard (confirmed physically present)
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint
   */
  async markOnYard(request: MarkOnYardRequest): Promise<CheckInOutResponse> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      const guardToken = guardAuthService.getToken();
      const response = await axios.post<CheckInOutResponse>(
        `${config.API_URL}${this.baseUrl}/mark-on-yard`,
        request,
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      const response = await api.post<CheckInOutResponse>(
        `${this.baseUrl}/mark-on-yard/employee`,
        request
      );
      return response.data;
    }
  }

  /**
   * Delete an item from yard inventory
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint
   */
  async deleteFromYard(recno: number): Promise<CheckInOutResponse> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      const guardToken = guardAuthService.getToken();
      const response = await axios.delete<CheckInOutResponse>(
        `${config.API_URL}${this.baseUrl}/inventory/${recno}`,
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      const response = await api.delete<CheckInOutResponse>(
        `${this.baseUrl}/inventory/${recno}/employee`
      );
      return response.data;
    }
  }

  /**
   * Get current yard inventory for a location, or for all yards when
   * yardrecno is omitted/null.
   */
  async getInventory(yardrecno?: number | null): Promise<YardInventoryItem[]> {
    const params: Record<string, number> = {};
    if (yardrecno != null) params.yardrecno = yardrecno;
    const response = await api.get<YardInventoryItem[]>(
      `${this.baseUrl}/inventory`,
      { params }
    );
    return response.data;
  }

  /**
   * Most recent yardevent datetime (any event type) for a yard. Used to show
   * "Inventory Last Performed" on the Yard Inventory page.
   */
  async getLastYardEvent(yardrecno: number): Promise<string | null> {
    const response = await api.get<{ last_event_datetime: string | null }>(
      `${this.baseUrl}/inventory/last-event`,
      { params: { yardrecno } }
    );
    return response.data.last_event_datetime;
  }

  /**
   * Reports snapshot — same shape as getInventory but the backend skips the
   * session-aware on_yard / on_yard_datetime subqueries (faster). Only
   * last_checked_datetime is populated.
   */
  async getInventoryReport(
    yardrecno?: number | null,
    signal?: AbortSignal,
    additionalFields?: string,
  ): Promise<YardInventoryItem[]> {
    const params: Record<string, number | string> = {};
    if (additionalFields) params.additional_fields = additionalFields;
    if (yardrecno != null) params.yardrecno = yardrecno;
    const response = await api.get<YardInventoryItem[]>(
      `${this.baseUrl}/inventory/report`,
      { params, signal }
    );
    return response.data;
  }

  /**
   * Counts for the Yard Reports stat row. Lightweight — no global filters,
   * just an optional set of yardrecnos. Empty list = all yards.
   */
  async getYardBucketCounts(
    yardrecnos: number[] = [],
    signal?: AbortSignal,
  ): Promise<YardBucketCounts> {
    // Build URLSearchParams manually so multi-value params serialize as
    // repeated `yardrecno=10&yardrecno=20` (FastAPI's expected list form).
    const params = new URLSearchParams();
    for (const r of yardrecnos) params.append('yardrecno', String(r));
    const response = await api.get<YardBucketCounts>(
      `${this.baseUrl}/inventory/bucket-counts`,
      { params, signal }
    );
    return response.data;
  }

  /**
   * Inventory rows for one of the three yardinv-based Yard Reports buckets.
   * Returned shape matches /inventory/report.
   */
  async getInventoryBucket(
    bucket: 'terminated-on-yard' | 'ssl-bare-chassis' | 'leg-yard-mismatch' | 'not-in-cms' | 'reefers-on-yard',
    yardrecno?: number | null,
    signal?: AbortSignal,
    additionalFields?: string,
  ): Promise<YardInventoryItem[]> {
    const params: Record<string, number | string> = {};
    if (additionalFields) params.additional_fields = additionalFields;
    if (yardrecno != null) params.yardrecno = yardrecno;
    const response = await api.get<YardInventoryItem[]>(
      `${this.baseUrl}/inventory/bucket/${bucket}`,
      { params, signal }
    );
    return response.data;
  }

  /**
   * Missing Containers — orderscurrent rows whose latest-arrived leg's
   * destination has no matching yardinv at any mapped destination yard.
   * Different shape from the other yard buckets (no yardinv to anchor on).
   */
  async getMissingContainers(
    yardrecno?: number | null,
    signal?: AbortSignal,
    additionalFields?: string,
  ): Promise<MissingContainerItem[]> {
    const params: Record<string, number | string> = {};
    if (additionalFields) params.additional_fields = additionalFields;
    if (yardrecno != null) params.yardrecno = yardrecno;
    const response = await api.get<MissingContainerItem[]>(
      `${this.baseUrl}/inventory/bucket/missing-containers`,
      { params, signal }
    );
    return response.data;
  }

  /**
   * Start a new yard inventory session. Inserts an 'R' yardevent that acts as
   * the new session-start marker, so all on-yard items return to Pending
   * Verification. Prior events are preserved for the audit log.
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint.
   */
  async resetInventory(yardrecno: number): Promise<{ session_recno: number; message: string }> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      const guardToken = guardAuthService.getToken();
      const response = await axios.post<{ session_recno: number; message: string }>(
        `${config.API_URL}${this.baseUrl}/inventory/${yardrecno}/reset`,
        {},
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      const response = await api.post<{ session_recno: number; message: string }>(
        `${this.baseUrl}/inventory/${yardrecno}/reset/employee`,
        {}
      );
      return response.data;
    }
  }

  /**
   * Create a reefer temperature log entry
   * Uses guard endpoint if guard is authenticated, otherwise uses employee endpoint
   */
  async logReeferTemp(request: ReeferLogRequest): Promise<ReeferLogCreateResponse> {
    const isGuard = guardAuthService.isAuthenticated();

    if (isGuard) {
      // Use guard endpoint with guard token
      const guardToken = guardAuthService.getToken();
      const response = await axios.post<ReeferLogCreateResponse>(
        `${config.API_URL}${this.baseUrl}/reefer-log`,
        request,
        { headers: { Authorization: `Bearer ${guardToken}` } }
      );
      return response.data;
    } else {
      // Use employee endpoint with Azure AD token (via api instance)
      const response = await api.post<ReeferLogCreateResponse>(
        `${this.baseUrl}/reefer-log/employee`,
        request
      );
      return response.data;
    }
  }

  /**
   * Get reefer temperature logs
   */
  async getReeferLogs(params?: {
    yardrecno?: number;
    equipno?: string;
    cmspronumber?: number;
    limit?: number;
  }): Promise<ReeferLogResponse[]> {
    const response = await api.get<ReeferLogResponse[]>(
      `${this.baseUrl}/reefer-logs`,
      { params }
    );
    return response.data;
  }

  /**
   * Get GWI yard groups for the reefer-log dropdown
   */
  async getReeferYards(): Promise<ReeferYard[]> {
    const response = await api.get<ReeferYard[]>(`${this.baseUrl}/reefer-yards`);
    return response.data;
  }

  /**
   * Get list of reefer orders at the given yard that need temperature logging.
   * yardrecno is a cms.yardlocations.recno (matches the Yard Inventory dropdown).
   */
  async getReefersInYard(yardrecno: number): Promise<ReeferInYard[]> {
    const response = await api.get<ReeferInYard[]>(
      `${this.baseUrl}/reefers-in-yard?yardrecno=${yardrecno}`
    );
    return response.data;
  }

  /**
   * Weekly yard-inventory completion grid for the Yard Inventory History page.
   * Pass a Sunday start_date and the matching Saturday end_date (YYYY-MM-DD).
   * A yard is "done" for a day when it had >=1 yardevent check-in that day.
   */
  async getInventoryHistoryWeekly(
    startDate: string,
    endDate: string,
    signal?: AbortSignal,
  ): Promise<YardInventoryHistoryResponse> {
    const response = await api.get<YardInventoryHistoryResponse>(
      `${this.baseUrl}/inventory/history/weekly`,
      { params: { start_date: startDate, end_date: endDate }, signal },
    );
    return response.data;
  }

  /**
   * Get yard event history
   */
  async getEvents(params?: {
    yardrecno?: number;
    equipno?: string;
    event_type?: string;
    cmspronumber?: number;
    limit?: number;
  }): Promise<YardEventItem[]> {
    const response = await api.get<YardEventItem[]>(
      `${this.baseUrl}/events`,
      { params }
    );
    return response.data;
  }

  // --- Admin CRUD on yardlocations ----------------------------------------

  async listYardLocationsAdmin(includeDeleted: boolean = true): Promise<YardLocationAdmin[]> {
    const response = await api.get<YardLocationAdmin[]>(
      `${this.baseUrl}/yard-locations`,
      { params: { include_deleted: includeDeleted } }
    );
    return response.data;
  }

  async createYardLocation(yard: string, locationIds: number[] = []): Promise<YardLocationAdmin> {
    const response = await api.post<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations`,
      { yard, location_ids: locationIds }
    );
    return response.data;
  }

  async updateYardLocation(
    recno: number,
    patch: { yard?: string },
  ): Promise<YardLocationAdmin> {
    const response = await api.put<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations/${recno}`,
      patch,
    );
    return response.data;
  }

  async addYardLocationLink(recno: number, locationId: number): Promise<YardLocationAdmin> {
    const response = await api.post<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations/${recno}/links`,
      { location_id: locationId },
    );
    return response.data;
  }

  async removeYardLocationLink(recno: number, locationId: number): Promise<YardLocationAdmin> {
    const response = await api.delete<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations/${recno}/links/${locationId}`,
    );
    return response.data;
  }

  /**
   * Master locations (cms.locations) for the linked-location picker on the
   * Yard Locations admin page. Active only.
   */
  async getMasterLocations(): Promise<MasterLocation[]> {
    const response = await api.get<MasterLocation[]>('/admin/locations');
    return response.data;
  }

  async softDeleteYardLocation(recno: number): Promise<YardLocationAdmin> {
    const response = await api.delete<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations/${recno}`
    );
    return response.data;
  }

  async restoreYardLocation(recno: number): Promise<YardLocationAdmin> {
    const response = await api.post<YardLocationAdmin>(
      `${this.baseUrl}/yard-locations/${recno}/restore`
    );
    return response.data;
  }
}

const yardService = new YardService();
export default yardService;
